import React from 'react'
import { View } from 'oio-react'
import { Helmet } from 'react-helmet'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import Discussions from 'src/apps/Discussions'
import GlobalViewport from 'src/apps/Global/components/Viewport'
import Group from 'src/apps/Group'
import Initiative from 'src/apps/Initiative'
import InitiativeType from 'src/apps/InitiativeType'
import Messages from 'src/apps/Messages'
import Page from 'src/apps/Page'
import Profile from 'src/apps/Profile'
import Search from 'src/apps/Search'
import { useGoogleAnalytics } from 'src/components/utils'
import { useMe, useOrganization } from 'src/graphql/hooks'
import { ThemeProvider } from 'src/libraries/contexts/Theme'
import Account from 'src/pages/Account'
import Explore from 'src/pages/Explore'
import Home from 'src/pages/Home'
import AccountInvitation from 'src/pages/Account/Invitation'
import NotFound from 'src/pages/NotFound'
import Settings from 'src/pages/Settings'
import Start from 'src/pages/Start'

// Note to the curious:
// We automagically statically swap DevTools with NotFound in production, so that
// it doesn't bloat the build bundle. See production webpack config for details.
import DevTools from 'src/pages/DevTools'

// Global components
import CookieBanner from '~/Global/components/CookieBanner'
import GlobalNav from '~/Global/components/Nav'

// =======================================================
// Secret Community Utils
// =======================================================

const allowedPathsInSecretCommunity = ['/account', '/invitation', '/login', '/dev']
const pathIsAllowedInSecretCommunity = pathname => (
   allowedPathsInSecretCommunity.some(allowedPath => pathname.startsWith(allowedPath))
)

// =======================================================
// Component
// =======================================================

const GlobalApp = () => {
   const { organization } = useOrganization()
   const location = useLocation()
   const { me, isLoggedIn, loading: userIsLoading } = useMe()
   const backgroundLocation = location?.state?.backgroundLocation
   useGoogleAnalytics()

   // If the community is a secret one, then restrict the paths to which the current
   // user is allowed to accesss if they are not logged in.
   if (organization.privacy === 'secret' && !pathIsAllowedInSecretCommunity(location.pathname)) {
      if (userIsLoading) {
         return null
      }

      if (!isLoggedIn) {
         return (
            <Redirect to="/account/login" />
         )
      }
   }

   const logoFaviconUrl = organization?.logoFavicon?.file.thumbnailUrlW240
   const discussionInitiativeTypeSlugParams = organization.initiativeTypes
      .filter(t => t.enabled && t.class === 'discussion')
      .map(t => t.slug)
      .join('|')

   return (
      <ThemeProvider>
         <Helmet
            defaultTitle={organization.name}
            link={[{
               rel: 'icon',
               type: 'image/png',
               href: logoFaviconUrl || '/assets/images/defaultFavicon.png'
            }]}
         />
         <GlobalNav />
         <View float="left" width="100%">
            <Switch location={backgroundLocation || location}>
               <Route path="/" exact component={Home} />
               <Redirect from="/login" to="/account/login" />
               <Route path="/account" component={Account} />
               <Route path="/home" component={Home} />
               <Route path="/initiatives/:slug" component={Initiative} />
               <Route path="/invitation" component={AccountInvitation} />
               <Route path="/messages" component={Messages} />
               <Route path="/pages/:pageSlug" component={Page} />
               <Redirect from="/profile" exact to="/profile/me" />
               {isLoggedIn && <Redirect from={`/profile/${me.id}`} exact to="/profile/me" />}
               <Route path="/profile/:userId" component={Profile} />
               <Route path="/search" component={Search} />
               <Route path="/settings" component={Settings} />
               <Route path="/start" component={Start} />

               {/* TODO:
                  Following 4 routes are temporary
                  This will be renamed once the new App concept is fully executed
                  These routes will incorporate initiative type
                  See PR: #390
                  See PR: #431
                  */}
               <Route path={['/apps', '/initiatives-new']}>
                  <GlobalViewport>
                     <Switch>
                        <Route path="/apps/group/:initiativeSlug" component={Group} />
                        <Route path={`/apps/:initiativeTypeSlug(${discussionInitiativeTypeSlugParams})`} component={Discussions} />
                        <Route path="/apps/:initiativeTypeSlug" component={InitiativeType} />
                        <Route path="/initiatives-new/:slug" component={Initiative} />
                     </Switch>
                  </GlobalViewport>
               </Route>

               {organization.globalUrls.map(globalUrl => (
                  <Route
                     key={globalUrl.url}
                     path={`/${globalUrl.url}`}
                     component={globalUrl.pageId && !globalUrl.initiativeId
                        ? Page
                        : Initiative
                     }
                  />
               ))}

               {/* We put the Explore route below custom pages so that we can
                  override the Explore page. Eventually this route will be deprecated anyway */}
               <Route path="/explore" component={Explore} />

               {/* We automatically statically replace DevTools with NotFound
                   in production builds */}
               <Route path="/dev" component={DevTools} />
               <Route component={NotFound} />
            </Switch>
            {/* TODO:
               Following 3 routes are temporary
               If there's a background location that exists, then we allow certain (additional)
               routes to be rendered in the foreground. This allows for animation transitions
               and/or modals for certain components.

               This will be renamed once the new App concept is fully executed
               These routes will incorporate initiative type
               See PR: #390
               See PR: #431
            */}
            {backgroundLocation && (
               <>
                  <Switch>
                     <Route path={`/apps/:initiativeTypeSlug(${discussionInitiativeTypeSlugParams})`} component={Discussions} />
                     <Route path="/apps/:initiativeTypeSlug">
                        <InitiativeType />
                     </Route>
                  </Switch>
                  <Route path="/initiatives-new/:slug">
                     <View
                        position="fixed"
                        top="0px"
                        left="0px"
                        right="0px"
                        bottom="0px"
                        backgroundColor="#fff"
                        scroll="on">
                        <Initiative />
                     </View>
                  </Route>
               </>
            )}
         </View>
         {/* TODO: This should be made generic
         See PR #360 */}
         {organization.slug === 'gns3' && <CookieBanner />}
      </ThemeProvider>
   )
}

export default GlobalApp
